import { computed, makeObservable, observable, reaction, runInAction } from "mobx"
import { createElement } from "react"
import { audioEngine } from "./audio-engine"
import { chunkedAudioEngine } from "./chunked-audio-engine"
import { indexDBService } from "@/entities/indexdb"
import { useUserStore } from "@/entities/user"
import { useModalStore } from "@/entities/modal"
import { ListenUntilModal } from "@/widgets"
import { Modal, UserPlan } from "@/shared/types"
import {
	ContentItemCreationStatus,
	type ContentItem,
} from "@/shared/api/generated"
import type { AudioPlaybackRate } from "./types"

type PlayerMode = "standard" | "chunked"

const parseListenUntil = (raw: unknown): number | null => {
	if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return raw
	if (typeof raw === "string" && raw.length > 0) {
		const n = Number(raw)
		return Number.isFinite(n) && n > 0 ? n : null
	}
	return null
}

/**
 * Single entry point for all audio playback.
 * Determines which engine to use, cleans up the other, handles resume.
 */
class ActivePlayer {
	mode: PlayerMode = "standard"
	/**
	 * Cap (in seconds, global position) past which a FREE-plan user is no
	 * longer allowed to listen to the active item. `null` means no cap.
	 * Server snapshots remaining daily budget at content creation and writes
	 * it to Content.listenUntil; we just enforce it here.
	 */
	currentListenUntil: number | null = null

	constructor() {
		makeObservable(this, {
			mode: observable,
			currentListenUntil: observable,
			isActive: computed,
			isPlaying: computed,
			isLoading: computed,
			isWaitingForChunk: computed,
			title: computed,
			id: computed,
			totalDuration: computed,
			currentPosition: computed,
			playbackRate: computed,
			isSeeking: computed,
		})

		// Single guard: any time the global position would land past
		// listenUntil for a FREE user — natural playback OR a seek action
		// (slider, +/-15, word click) — pause, pin the cursor to the cap,
		// open the upgrade modal. The item stays loaded; the only way past
		// the cap is resume() restarting from 0.
		// `pos <= cap` (instead of `<`) is the dedupe: after we pin to cap,
		// the reaction re-fires with pos === cap and exits.
		reaction(
			() => this.currentPosition,
			pos => {
				const cap = this.currentListenUntil
				if (cap === null) return
				if (useUserStore.getState().plan !== UserPlan.FREE) return
				if (pos <= cap) return

				this.pauseInternal()
				if (this.mode !== "chunked") {
					// Standard engine: snap cursor exactly to cap.
					audioEngine.seekTo(cap)
				}
				// Chunked engine: pause stops the RAF, position freezes at
				// cap+ε. No reaction loop because position observable
				// stops changing.
				useModalStore
					.getState()
					.openModal(Modal.LISTEN_UNTIL, createElement(ListenUntilModal))
			}
		)
	}

	// ─── Unified entry point ──────────────────────────────────

	/**
	 * Start or resume playback for a content item.
	 * - If already playing this item → toggle pause/resume
	 * - Otherwise → stop everything, pick the right engine, start
	 */
	/**
	 * Start or resume playback for a content item.
	 * - If already playing this item → toggle pause/resume
	 * - Otherwise → stop everything, pick the right engine, start
	 * - force: skip the pause/resume check, always restart (used on status transition)
	 */
	async play(item: ContentItem, options?: { force?: boolean; startPosition?: number }) {
		const isChunked =
			item.creationStatus ===
			ContentItemCreationStatus.PARTIALLY_COMPLETED_AND_STILL_PROCESSING

		// Resume / pause if this item is already active (unless forced)
		if (!options?.force && this.id === item.id && this.isActive) {
			if (this.isPlaying) {
				this.pause()
			} else {
				this.resume()
			}
			return
		}

		// Clean up whichever engine was active
		this.stopAll()

		const cap = parseListenUntil(item.listenUntil)
		const startPos = await this.resolveStartPosition(item, cap, options?.startPosition)

		runInAction(() => {
			this.currentListenUntil = cap
		})

		if (isChunked) {
			this.mode = "chunked"
			await chunkedAudioEngine.start(item.id, item.title, {
				startPosition: startPos,
			})
		} else {
			this.mode = "standard"
			audioEngine.startPlay(
				item.audioUrl,
				{
					title: item.title,
					totalDuration: item.audioTime,
					id: item.id,
				},
				{
					startPosition: startPos,
				}
			)
		}
	}

	/** Load audio into engine without playing. Common player shows the item. */
	async load(item: ContentItem, options?: { force?: boolean; startPosition?: number }) {
		if (!options?.force && this.id === item.id && this.isActive) return

		this.stopAll()

		const cap = parseListenUntil(item.listenUntil)
		const startPos = await this.resolveStartPosition(item, cap, options?.startPosition)

		const isChunked =
			item.creationStatus ===
			ContentItemCreationStatus.PARTIALLY_COMPLETED_AND_STILL_PROCESSING

		runInAction(() => {
			this.currentListenUntil = cap
		})

		if (isChunked) {
			this.mode = "chunked"
			await chunkedAudioEngine.load(item.id, item.title, startPos)
		} else {
			this.mode = "standard"
			audioEngine.load(
				item.audioUrl,
				{
					title: item.title,
					totalDuration: item.audioTime,
					id: item.id,
				},
				startPos
			)
		}
	}

	/**
	 * Pick a starting position for a fresh play/load.
	 * If a FREE user's saved position is at or past the listenUntil cap, we
	 * restart from 0 — otherwise pressing play would re-trigger the limit
	 * modal immediately and the user feels stuck.
	 */
	private async resolveStartPosition(
		item: ContentItem,
		cap: number | null,
		override?: number
	): Promise<number> {
		const saved =
			override ?? (await indexDBService.getAudioPosition(item.id)) ?? 0
		const isFree = useUserStore.getState().plan === UserPlan.FREE
		if (isFree && cap !== null && saved >= cap) return 0
		return saved
	}

	/** Stop both engines, reset mode */
	stopAll() {
		audioEngine.reset()
		chunkedAudioEngine.stop()
		runInAction(() => {
			this.currentListenUntil = null
		})
	}

	// ─── Computed state (delegates to active engine) ──────────

	get id(): string | null {
		if (this.mode === "chunked") return chunkedAudioEngine.contentId
		return audioEngine.id
	}

	get isActive(): boolean {
		if (this.mode === "chunked") return chunkedAudioEngine.contentId !== null
		return audioEngine.currentUrl !== null
	}

	get isPlaying(): boolean {
		if (this.mode === "chunked") return chunkedAudioEngine.isPlaying
		return audioEngine.isPlaying
	}

	get isLoading(): boolean {
		if (this.mode === "chunked") return chunkedAudioEngine.isLoading
		return audioEngine.isLoading
	}

	/** True while ChunkedAudioEngine is stalled waiting for the next chunk
	 * (or retrying one that failed to load) - distinct from isLoading/
	 * isPlaying so the UI can show a loader instead of looking merely paused. */
	get isWaitingForChunk(): boolean {
		if (this.mode === "chunked") return chunkedAudioEngine.isWaitingForChunk
		return false
	}

	/** True while the playing chunk was delivered audio-first and its word
	 * highlighting is still being generated server-side. */
	get isHighlightPending(): boolean {
		if (this.mode === "chunked") return chunkedAudioEngine.isHighlightPending
		return false
	}

	get title(): string | null {
		if (this.mode === "chunked") return chunkedAudioEngine.title
		return audioEngine.title
	}

	get totalDuration(): number {
		if (this.mode === "chunked") return chunkedAudioEngine.totalDuration
		return audioEngine.totalDuration
	}

	get currentPosition(): number {
		if (this.mode === "chunked") return chunkedAudioEngine.globalPosition
		return audioEngine.currentPosition
	}

	get playbackRate(): AudioPlaybackRate {
		if (this.mode === "chunked") return chunkedAudioEngine.playbackRate
		return audioEngine.playbackRate
	}

	get isSeeking(): boolean {
		if (this.mode === "chunked") return false
		return audioEngine.isSeeking
	}

	// ─── Delegated actions ────────────────────────────────────

	pause() {
		this.pauseInternal()
	}

	private pauseInternal() {
		if (this.mode === "chunked") {
			chunkedAudioEngine.pause()
		} else {
			audioEngine.pause()
		}
	}

	resume() {
		const cap = this.currentListenUntil
		const isFree = useUserStore.getState().plan === UserPlan.FREE
		if (cap !== null && isFree && this.currentPosition >= cap) {
			// Press Play while pinned at cap → restart from 0.
			if (this.mode === "chunked") {
				// Chunked seekTo auto-plays via playFromGlobalPosition.
				chunkedAudioEngine.seekTo(0)
			} else {
				audioEngine.seekTo(0)
				audioEngine.resume()
			}
			return
		}
		if (this.mode === "chunked") {
			chunkedAudioEngine.resume()
		} else {
			audioEngine.resume()
		}
	}

	seekTo(position: number) {
		// The reaction takes care of clamping if `position > cap`.
		if (this.mode === "chunked") {
			chunkedAudioEngine.seekTo(position)
		} else {
			audioEngine.seekTo(position)
		}
	}

	/**
	 * Seek + auto-play if currently paused. If the seek target is past the
	 * listenUntil cap for a FREE user, we *only* seek (which lets the
	 * reaction clamp + open the modal) and skip the resume — the user was
	 * trying to listen further than allowed, not asking to play from cap
	 * (which resume would interpret as "restart from 0").
	 */
	seekToAndPlay(position: number) {
		const cap = this.currentListenUntil
		const isFree = useUserStore.getState().plan === UserPlan.FREE
		const wouldExceedCap = cap !== null && isFree && position > cap

		this.seekTo(position)
		if (wouldExceedCap) return
		if (!this.isPlaying) this.resume()
	}

	setPlaybackRate(rate: AudioPlaybackRate) {
		if (this.mode === "chunked") {
			chunkedAudioEngine.setPlaybackRate(rate)
		} else {
			audioEngine.setPlaybackRate(rate)
		}
	}
}

export const activePlayer = new ActivePlayer()
